import React, { useEffect, useState } from 'react';
import dayAwayService from '@/services/dayAwayService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, RefreshCw } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

interface DashboardSheetRow {
  person: string;
  allTime: {
    full: number;
    half: number;
    emailOnly: number;
    total: number;
  };
  thisYear: {
    full: number;
    half: number;
    emailOnly: number;
    total: number;
  };
  lastYear: {
    full: number;
    half: number;
    emailOnly: number;
    total: number;
  };
}

const DashboardSheet: React.FC = () => {
  const [dashboardSheetData, setDashboardSheetData] = useState<DashboardSheetRow[]>([]);
  const [filterText, setFilterText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchDashboardSheetData = async () => {
    try {
      console.log('Fetching dashboard sheet data from Google Sheets Dashboard page...');
      const data = await dayAwayService.getDashboardSheetData();
      
      // Sort by this year's total (descending)
      const sortedData = data.sort((a: DashboardSheetRow, b: DashboardSheetRow) => b.thisYear.total - a.thisYear.total);
      
      setDashboardSheetData(sortedData);
      console.log(`Successfully loaded ${sortedData.length} dashboard sheet entries`);
    } catch (error) {
      console.error('Error fetching dashboard sheet data:', error);
      setDashboardSheetData([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardSheetData();
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchDashboardSheetData();
  };

  const filteredSummary = dashboardSheetData.filter(row =>
    row.person && row.person.toLowerCase().includes(filterText.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard Sheet</h1>
          <p className="text-gray-500 dark:text-gray-400">Loading time off summary from Google Sheets...</p>
        </div>
        <Card>
          <CardContent className="p-8">
            <div className="flex items-center justify-center">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
              <span className="ml-2 text-lg">Loading dashboard data...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard Sheet</h1>
          <p className="text-gray-500 dark:text-gray-400">Time off summary from Google Sheets Dashboard page</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-500 dark:text-blue-400" />
            Time Off Summary ({filteredSummary.length} employees)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Input
              placeholder="Filter by person..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="max-w-sm"
            />
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 dark:bg-muted/20">
                  <TableHead className="border-r dark:border-gray-700"></TableHead>
                  <TableHead colSpan={4} className="text-center border-r dark:border-gray-700 bg-blue-50 dark:bg-blue-950/50">All Time</TableHead>
                  <TableHead colSpan={4} className="text-center border-r dark:border-gray-700 bg-green-50 dark:bg-green-950/50">This Year</TableHead>
                  <TableHead colSpan={4} className="text-center bg-amber-50 dark:bg-amber-950/50">Last Year</TableHead>
                </TableRow>
                <TableRow>
                  <TableHead className="border-r dark:border-gray-700 font-bold">Person</TableHead>
                  <TableHead className="text-right border-r-0">Full</TableHead>
                  <TableHead className="text-right border-r-0">Half</TableHead>
                  <TableHead className="text-right border-r-0">Email</TableHead>
                  <TableHead className="text-right border-r dark:border-gray-700 font-bold bg-blue-50/50 dark:bg-blue-950/30">Total</TableHead>
                  <TableHead className="text-right border-r-0">Full</TableHead>
                  <TableHead className="text-right border-r-0">Half</TableHead>
                  <TableHead className="text-right border-r-0">Email</TableHead>
                  <TableHead className="text-right border-r dark:border-gray-700 font-bold bg-green-50/50 dark:bg-green-950/30">Total</TableHead>
                  <TableHead className="text-right border-r-0">Full</TableHead>
                  <TableHead className="text-right border-r-0">Half</TableHead>
                  <TableHead className="text-right border-r-0">Email</TableHead>
                  <TableHead className="text-right font-bold bg-amber-50/50 dark:bg-amber-950/30">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSummary.map((row, index) => (
                  <TableRow key={row.person || index} className="hover:bg-muted/5 dark:hover:bg-muted/20 transition-colors duration-150 cursor-pointer">
                    <TableCell className="border-r dark:border-gray-700 font-medium">{row.person}</TableCell>
                    <TableCell className="text-right">{row.allTime.full}</TableCell>
                    <TableCell className="text-right">{row.allTime.half}</TableCell>
                    <TableCell className="text-right">{row.allTime.emailOnly}</TableCell>
                    <TableCell className="text-right border-r dark:border-gray-700 font-bold bg-blue-50/30 dark:bg-blue-950/20">{row.allTime.total.toFixed(1)}</TableCell>
                    <TableCell className="text-right">{row.thisYear.full}</TableCell>
                    <TableCell className="text-right">{row.thisYear.half}</TableCell>
                    <TableCell className="text-right">{row.thisYear.emailOnly}</TableCell>
                    <TableCell className="text-right border-r dark:border-gray-700 font-bold bg-green-50/30 dark:bg-green-950/20">{row.thisYear.total.toFixed(1)}</TableCell>
                    <TableCell className="text-right">{row.lastYear.full}</TableCell>
                    <TableCell className="text-right">{row.lastYear.half}</TableCell>
                    <TableCell className="text-right">{row.lastYear.emailOnly}</TableCell>
                    <TableCell className="text-right font-bold bg-amber-50/30 dark:bg-amber-950/20">{row.lastYear.total.toFixed(1)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardSheet;
